/*******************************************************
 * FINAL MERGED CLEAN FILE — OPTION A (OD RATE TABLE)
 * PART 1 — IMPORTS + STATES + AUTO LOGIC
 *******************************************************/

/************* IMPORTS *************/
 import React, { useState, useEffect } from "react";
 import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "../../utils/api";
import { FaFilePdf, FaSave } from "react-icons/fa";
import "./QuotationBuilder.css";
import { COMPANY_LOGOS, generateQuotationPDF } from "../../utils/generateQuotationPdf";

/************* COMPONENT START *************/
 const QuotationBuilder = () => {

  const insuranceCompanies = Object.keys(COMPANY_LOGOS);

  /*******************************************************
   * AUTO OD RATE TABLE (Zone A/B + CC + Age)
  *******************************************************/
  const OD_RATE_TABLE = {
    "<5": {
      "0-1000": { A: 3.127, B: 3.039 },
      "1000-1500": { A: 3.283, B: 3.191 },
      "1500+": { A: 3.44, B: 3.343 },
    },
    "5-10": {
      "0-1000": { A: 3.283, B: 3.191 },
      "1000-1500": { A: 3.447, B: 3.351 },
      "1500+": { A: 3.612, B: 3.51 },
    },
    ">10": {
      "0-1000": { A: 3.362, B: 3.267 },
      "1000-1500": { A: 3.529, B: 3.43 },
      "1500+": { A: 3.698, B: 3.594 },
    },
  };

  const getCCRange = (cc) => {
    cc = Number(cc);
    if (cc <= 1000) return "0-1000";
    if (cc <= 1500) return "1000-1500";
    return "1500+";
  };

  const getAgeRange = (age) => {
    if (age < 5) return "<5";
    if (age <= 10) return "5-10";
    return ">10";
  };

  const getAutoODRate = (age, cc, zone) => {
    if (!age || !cc || !zone) return 0;
    const ageRange = getAgeRange(age);
    const ccRange = getCCRange(cc);
    const zoneKey = zone === "Zone A" ? "A" : "B";
    return OD_RATE_TABLE[ageRange][ccRange][zoneKey];
  };

  /*******************************************************
   * NCB TABLE
  *******************************************************/
  const NCB_TABLE = {
    "0": 20,
    "20": 25,
    "25": 35,
    "35": 45,
    "45": 50,
    "50": 50,
  };

  /*******************************************************
   * ADD-ONS FULL NAME → SUMMARY CODE MAPPING
  *******************************************************/
  const ADDON_SUMMARY_CODES = {
    zeroDepreciation: "ZD",
    engineProtect: "EP",
    rti: "RTI",
    consumables: "CM",
    tyreProtect: "TYRE",
    keyProtect: "KP",
    rodentDamage: "RB",
    personalBelongings: "PB",
    emergencyHotelExpenses: "E/H",
    roadSideAssistance: "RSA",
  };

  const ADDON_LABELS = {
    zeroDepreciation: "Zero Depreciation",
    engineProtect: "Engine Protection",
    rti: "Return To Invoice (RTI)",
    consumables: "Consumable",
    tyreProtect: "Tyre Protection",
    keyProtect: "Key Protection",
    personalBelongings: "Personal Belonging",
    rodentDamage: "Rodent-bite",
    emergencyHotelExpenses: ["Emergency Transport", "Hotel Expenses"],
    roadSideAssistance: "Road Side Assistance",
  };

  /*******************************************************
   * TEXT CAPITALIZATION HELPERS
  *******************************************************/
  const capitalizeFirst = (value) => {
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const CAPITALIZE_FIELDS = new Set([
    "customerName",
    "vehicleDetails.registrationNumber",
    "vehicleDetails.make",
    "vehicleDetails.model",
    "vehicleDetails.variant",
    "vehicleDetails.city",
  ]);

  const shouldCapitalizeField = (fieldName) => CAPITALIZE_FIELDS.has(fieldName);

  /*******************************************************
   * STATES
  *******************************************************/
  const [formData, setFormData] = useState({
    insuranceCompany: "",
    customerName: "",
    customerEmail: "",
    customerMobile: "+91 ",

    vehicleDetails: {
      registrationNumber: "",
      make: "",
      model: "",
      variant: "",
      fuelType: "",
      registrationYear: "",
      cubicCapacity: "",
      seatingCapacity: "",
      policyExpiryDate: "",
      previousClaimPercent: "",
      policyType: "",
      city: "",
      zone: "",
      vehicleAge: 0,
      hypothecationLease: "",
    },

    idvDetails: {
      vehicleIDV: 0,
      electrical: 0,
      nonElectrical: 0,
      biFuel: 0,
      totalIDV: 0,
    },

    premiumDetails: {
      elecAccessories: 0,
      nonElecAccessories: 0,
      biFuelKit: 0,
      basicODVehicle: 0,
      subTotalBasic: 0,
      discountNCB: 0,
      discountAntiTheft: 0,
      subTotalDiscounts: 0,
      grossPremium: 0,
    },

    discounts: {
      ncbPercentage: 0,
      ncbAmount: 0,
    },

    od_rate: 0,
    od_rate_amount: 0,
    od_discount: 0,
    od_final: 0,

    addon_rate: "0",
    addon_amount: 0,

    addOns: {
      zeroDepreciation: { included: false, premium: 0 },
      engineProtect: { included: false, premium: 0 },
      rti: { included: false, premium: 0 },
      consumables: { included: false, premium: 0 },
      tyreProtect: { included: false, premium: 0 },
      keyProtect: { included: false, premium: 0 },
      rodentDamage: { included: false, premium: 0 },
      personalBelongings: { included: false, premium: 0 },
      emergencyHotelExpenses: { included: false, premium: 0 },
      roadSideAssistance: { included: false, premium: 0 },
    },

    legalLiabilities: {
      paidDriver: { included: false, premium: 0 },
      employees: { included: false, count: 0, premium: 0 },
    },

    ncb: 0,

    liab_bifuel: 0,

    net_liability_B: 0,

    taxes: {
      sgst: 0,
      cgst: 0,
    },
  });

  const [premium, setPremium] = useState({
    ownDamage: 0,
    thirdParty: 0,
    ncbDiscount: 0,
    addOnsTotal: 0,
    legalLiabilitiesTotal: 0,
    subtotal: 0,
    totalPremium: 0,
  });

  const [bhRegistration, setBhRegistration] = useState("NO");
  const [specialRegNo, setSpecialRegNo] = useState(false);

  /*******************************************************
   * AUTO CITY ZONE + AGE
  *******************************************************/
  const currentYear = new Date().getFullYear();

  const zoneACities = [
  "MUMBAI",
  "DELHI",
  "BANGALORE",
  "CHENNAI",
  "KOLKATA",
  "AHMEDABAD",
  "PUNE",
  "HYDERABAD",
];

const detectZone = (city) => {
  if (!city) return "";
  return zoneACities.includes(city.toUpperCase()) ? "Zone A" : "Zone B";
};

  const calculateVehicleAge = (year) =>
    !year ? 0 : currentYear - Number(year);

  /*******************************************************
   * AUTO NCB UPDATE
  *******************************************************/
  useEffect(() => {
    const percent = formData.vehicleDetails.previousClaimPercent;

    let ncbValue = 0;

    if (percent === "CLAIM TAKEN") {
      ncbValue = 0;
    } else if (percent in NCB_TABLE) {
      ncbValue = NCB_TABLE[percent];
    }

    setFormData((prev) => ({ ...prev, ncb: ncbValue }));
  }, [formData.vehicleDetails.previousClaimPercent]);

  /*******************************************************
   * AUTO OD RATE UPDATE
  *******************************************************/
  useEffect(() => {
    const { vehicleAge, cubicCapacity, zone } = formData.vehicleDetails;
    const rate = getAutoODRate(vehicleAge, cubicCapacity, zone);

    setFormData((prev) => ({
      ...prev,
      od_rate: rate || 0,
    }));
  }, [
    formData.vehicleDetails.vehicleAge,
    formData.vehicleDetails.cubicCapacity,
    formData.vehicleDetails.zone,
  ]);

  /*******************************************************
   * AUTO SAVE NCB AMOUNT → STATE
  *******************************************************/
  useEffect(() => {
    const basic = formData.premiumDetails.subTotalBasic || 0;
    const ncbPercent = formData.ncb || 0;

    const ncbAmount = Math.round((basic * ncbPercent) / 100);

    setFormData((prev) => ({
      ...prev,
      discounts: {
        ...prev.discounts,
        ncbAmount,
      },
    }));
  }, [formData.premiumDetails.subTotalBasic, formData.ncb]);

  /*******************************************************
   * MOBILE NUMBER HANDLER (Keep +91 prefix, max 10 digits)
   *******************************************************/
  const handleMobileChange = (e) => {
    let { value } = e.target;
    
    // Ensure +91 prefix is always present
    if (!value.startsWith("+91 ")) {
      value = "+91 ";
    }
    
    // Extract digits after +91 
    const digits = value.replace("+91 ", "").replace(/\D/g, "");
    
    // Limit to 10 digits
    const limitedDigits = digits.slice(0, 10);
    
    // Reconstruct value with prefix
    const finalValue = "+91 " + limitedDigits;
    
    setFormData((prev) => ({
      ...prev,
      customerMobile: finalValue,
    }));
  };

  /*******************************************************
   * REGISTRATION NUMBER FORMATTER
   *******************************************************/
  const formatRegistration = (value, isBH, isSpecial) => {
    const input = (value ?? "").toString().toUpperCase();

    // Special Reg No: Only 1-4 digits, keep existing behavior
    if (isSpecial) {
      return input.replace(/\D/g, "").slice(0, 4);
    }

    // BH Registration: XXBH + up to 4 digits + 1 letter, no hyphens
    if (isBH) {
      const cleaned = input.replace(/[^A-Z0-9]/g, "");
      const match = cleaned.match(/^(\d{2}BH)([A-Z0-9]*)/);

      if (!match) {
        return cleaned;
      }

      const prefix = match[1];
      const remainder = match[2] || "";
      const digits = remainder.replace(/\D/g, "").slice(0, 4);
      const letter = remainder.replace(/[^A-Z]/g, "").charAt(0) || "";

      return `${prefix}${digits}${letter}`;
    }

    // Normal mode: Manual entry, uppercase only
    return input;
  };

  /*******************************************************
   * INPUT HANDLER (IDV Auto + Add-on Rate Decimal Logic)
   *******************************************************/
  const handleInputChange = (e) => {
    let { name, value } = e.target;

    // Convert email to lowercase
    if (name === "customerEmail" && typeof value === "string") {
      value = value.toLowerCase();
    } else if (typeof value === "string" && name !== "customerEmail") {
      // Convert to uppercase for all text fields (except email)
      // Skip capitalizeFirst for text fields that should be fully uppercase
      value = value.toUpperCase();
    }

    // Special condition: ensure all select dropdown values are always uppercase
    if (e.target.tagName === "SELECT" && typeof value === "string" && value !== "") {
      value = value.toUpperCase();
    }

    /* VEHICLE DETAILS */
    if (name.startsWith("vehicleDetails.")) {
      const field = name.split(".")[1];

      const regYear =
        name === "vehicleDetails.registrationYear"
          ? value
          : formData.vehicleDetails.registrationYear;

      const vehicleAge = calculateVehicleAge(regYear);

      setFormData((prev) => {
  const updatedCity = field === "city" ? value : prev.vehicleDetails.city;

  return {
    ...prev,
    vehicleDetails: {
      ...prev.vehicleDetails,
      [field]: value,
      vehicleAge,
      zone: detectZone(updatedCity),  // ⭐ FIXED — always use updated city
    },
  };
});

      return;
    }

    /* IDV DETAILS */
    if (name.startsWith("idvDetails.")) {
      const field = name.split(".")[1];

      const updated = {
        ...formData.idvDetails,
        [field]: value,
      };

      const vehicleIDV = parseInt(updated.vehicleIDV) || 0;
      const electricalIDV = parseInt(updated.electrical) || 0;
      const nonElectricalIDV = parseInt(updated.nonElectrical) || 0;
      const biFuelIDV = parseInt(updated.biFuel) || 0;

      const totalIDV =
        vehicleIDV + electricalIDV + nonElectricalIDV + biFuelIDV;

      const electricalPremium = electricalIDV * 0.04;
      const nonElectricalPremium = nonElectricalIDV * 0.03;
      const biFuelPremium = biFuelIDV * 0.04;

      setFormData((prev) => ({
        ...prev,
        idvDetails: {
          ...updated,
          totalIDV,
        },
        premiumDetails: {
          ...prev.premiumDetails,
          elecAccessories: Math.round(electricalPremium),
          nonElecAccessories: Math.round(nonElectricalPremium),
          biFuelKit: Math.round(biFuelPremium),
        },
      }));

      return;
    }

    /* ADD-ON RATE INPUT — Decimal Allowed */
    if (name === "addon_rate") {
      let rate = value;

      // Allow only digits + single decimal
      rate = rate.replace(/[^0-9.]/g, "");

      const parts = rate.split(".");
      if (parts.length > 2) return;

      const parsed = parseFloat(rate) || 0;
      const totalIDV = formData.idvDetails.totalIDV || 0;

      const amount = Math.round((totalIDV * parsed) / 100);

      setFormData((prev) => ({
        ...prev,
        addon_rate: rate,
        addon_amount: amount,
      }));

      return;
    }

    /* MANUAL NCB % OVERRIDE */
    if (name === "ncbPercentage") {
      const percent = parseFloat(value) || 0;
      const basic = formData.premiumDetails.subTotalBasic || 0;

      const ncbAmount = Math.round((basic * percent) / 100);

      setFormData((prev) => ({
        ...prev,
        discounts: {
          ...prev.discounts,
          ncbPercentage: percent,
          ncbAmount,
        },
      }));

      return;
    }

    /* OTHER DIRECT VALUES */
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

    /*******************************************************
   * OD RATE AMOUNT + DISCOUNT + FINAL OD
  *******************************************************/
  useEffect(() => {
    const idv = formData.idvDetails.totalIDV;
    const rate = formData.od_rate;

    const rateAmount = (idv * rate) / 100;

    const discountPercent = Number(formData.od_discount || 0);
    const discountAmount = (rateAmount * discountPercent) / 100;

    const finalOD = rateAmount - discountAmount;
    const basicODVehicle = Math.round(finalOD);

    const elec = formData.premiumDetails.elecAccessories || 0;
    const nonElec = formData.premiumDetails.nonElecAccessories || 0;
    const biFuel = formData.premiumDetails.biFuelKit || 0;

    const subTotalBasic = basicODVehicle + elec + nonElec + biFuel;

    setFormData((prev) => ({
      ...prev,
      od_rate_amount: Math.round(rateAmount),
      od_final: Math.round(finalOD),
      premiumDetails: {
        ...prev.premiumDetails,
        basicODVehicle,
        subTotalBasic: Math.round(subTotalBasic),
      },
    }));
  }, [
    formData.idvDetails.totalIDV,
    formData.od_rate,
    formData.od_discount,
  ]);

  useEffect(() => {
  const odBiFuelPremium = Number(formData.premiumDetails.biFuelKit) || 0;

  const liabilityAmount = odBiFuelPremium > 0 ? 60 : 0;

  setFormData(prev => ({
    ...prev,
    liab_bifuel: liabilityAmount
  }));
}, [
  formData.premiumDetails.biFuelKit,
  formData.idvDetails.biFuel
]);

useEffect(() => {

  const tp = Number(premium.thirdParty) || 0;
  const bifuel = Number(formData.liab_bifuel) || 0;

  const paOwner = Number(formData.liab_pa_owner) || 0;
  const paPassenger = Number(formData.liab_pa_passenger) || 0;
  const paDriver = Number(formData.liab_pa_driver) || 0;

  const imt28 = Number(formData.ll_paid_driver) || 0;
  const imt29 = Number(formData.ll_employees) || 0;

  const total =
    tp +
    bifuel +
    paOwner +
    paPassenger +
    paDriver +
    imt28 +
    imt29;

  setFormData((prev) => ({
    ...prev,
    net_liability_B: total
  }));
}, [
  premium.thirdParty,
  formData.liab_bifuel,

  formData.liab_pa_owner,
  formData.liab_pa_passenger,
  formData.liab_pa_driver,

  formData.ll_paid_driver,
  formData.ll_employees
]);

  /*******************************************************
   * AUTO SUB TOTAL DISCOUNTS — NCB + ANTI THEFT
  *******************************************************/
  useEffect(() => {
    const ncbAmt = formData.discounts.ncbAmount || 0;
    const antiTheft = Number(formData.premiumDetails.discountAntiTheft) || 0;

    const subTotal = ncbAmt + antiTheft;

    setFormData((prev) => ({
      ...prev,
      premiumDetails: {
        ...prev.premiumDetails,
        subTotalDiscounts: subTotal,
      },
    }));
  }, [
    formData.discounts.ncbAmount,
    formData.premiumDetails.discountAntiTheft,
  ]);

  /*******************************************************
   * ADD-ON PREMIUM CALCULATION LOGIC
  *******************************************************/
  const calculateAddOnPremium = (idv, name) => {
    if (!idv) return 0;

    const rates = {
      zeroDepreciation: 0.15,
      engineProtect: 0.05,
      rti: 0.03,
      consumables: 0.02,
      tyreProtect: 0.04,
      keyProtect: 0.01,
      rodentDamage: 0.02,
      personalBelongings: 0.02,
      emergencyHotelExpenses: 0.03,
      roadSideAssistance: 0,
    };

    return idv * (rates[name] || 0);
  };

  /*******************************************************
   * ADD-ON CHANGE HANDLER
  *******************************************************/
  const handleAddOnChange = (name, included) => {
    const idv = formData.idvDetails.totalIDV;

    const premiumValue = included
      ? calculateAddOnPremium(idv, name)
      : 0;

    setFormData((prev) => ({
      ...prev,
      addOns: {
        ...prev.addOns,
        [name]: {
          included,
          premium: Math.round(premiumValue),
        },
      },
    }));
  };

  /*******************************************************
   * CALCULATE TOTAL PREMIUM (FINAL)
  *******************************************************/
  const calculatePremium = () => {
    const V = formData.vehicleDetails;
    const I = formData.idvDetails;

    // Basic OD (Simplified OD 3% rate)
    const ownDamage = (I.totalIDV * 3) / 100;

    // Third Party Premium by CC
    const ccRaw = V.cubicCapacity;
    const cc = Number((ccRaw || "").toString().trim());
    let thirdParty = 0;
    if (!cc || cc === 0 || isNaN(cc)) {
      thirdParty = 0;
    } else if (cc <= 1000) {
      thirdParty = 2094;
    } else if (cc <= 1500) {
      thirdParty = 3416;
    } else {
      thirdParty = 7897;
    }

    // NCB Discount
    const ncbDiscount = ownDamage * (formData.ncb / 100);

    // Add-ons
    const addOnsTotal = Object.values(formData.addOns)
      .filter((a) => a.included)
      .reduce((sum, a) => sum + a.premium, 0);

    // Legal liabilities
    const legalTotal =
      (formData.legalLiabilities.paidDriver.included
        ? formData.legalLiabilities.paidDriver.premium
        : 0) +
      (formData.legalLiabilities.employees.included
        ? formData.legalLiabilities.employees.premium
        : 0);

    // Final subtotal
    const subtotal =
      ownDamage - ncbDiscount + thirdParty + addOnsTotal + legalTotal;

    const totalPremium = Math.round(subtotal);

    setPremium({
      ownDamage: Math.round(ownDamage),
      thirdParty: Math.round(thirdParty),
      ncbDiscount: Math.round(ncbDiscount),
      addOnsTotal: Math.round(addOnsTotal),
      legalLiabilitiesTotal: Math.round(legalTotal),
      subtotal: Math.round(subtotal),
      totalPremium: totalPremium,
    });
  };

  useEffect(() => {
    calculatePremium();
  }, [formData]);

  /*******************************************************
   * CALCULATE SGST, CGST, AND GROSS PREMIUM
   *******************************************************/
  useEffect(() => {
    // Calculate total premium based on policy type
    const totalOwnDamagePremiumCalc =
      (formData.premiumDetails.subTotalBasic || 0) -
      (formData.discounts.ncbAmount || 0);
    const netOwnDamagePremiumCalc =
      totalOwnDamagePremiumCalc + (formData.addon_amount || 0);

    const totalPremium = formData.vehicleDetails.policyType === "THIRD PARTY" 
      ? (formData.net_liability_B || 0)
      : formData.vehicleDetails.policyType === "OWN DAMAGE ONLY"
      ? netOwnDamagePremiumCalc
      : netOwnDamagePremiumCalc + (formData.net_liability_B || 0);

    const sgstAmount = Math.round(totalPremium * 0.09);
    const cgstAmount = Math.round(totalPremium * 0.09);
    const grossPremium = totalPremium + sgstAmount + cgstAmount;

    setFormData((prev) => ({
      ...prev,
      taxes: {
        sgst: sgstAmount,
        cgst: cgstAmount,
      },
      premiumDetails: {
        ...prev.premiumDetails,
        grossPremium: grossPremium,
      },
    }));
  }, [
    formData.vehicleDetails.policyType,
    formData.net_liability_B,
    formData.premiumDetails.subTotalBasic,
    formData.discounts.ncbAmount,
    formData.addon_amount,
  ]);

  const buildFinalPDFData = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();
    const sequence = String(Date.now() % 100).padStart(2, "0");

    const quoteMeta = {
      quoteNo: `BRQ/1508/${day}-${month}/${sequence}`,
      quoteDate: `${day}-${month}-${year}`,
    };

    return {
      ...formData,
      paOwnerDriver: Number(formData.liab_pa_owner) || 0,
      paUnnamedPassenger: Number(formData.liab_pa_passenger) || 0,
      paPaidDriver: Number(formData.liab_pa_driver) || 0,
      llPaidDriver: Number(formData.ll_paid_driver) || 0,
      llEmployees: Number(formData.ll_employees) || 0,
      quoteMeta,
      premiumBreakdown: premium,
    };
  };

  /*******************************************************
   * SAVE QUOTATION
   *******************************************************/
  const handleSave = async () => {
    try {
      const finalPDFData = buildFinalPDFData();
      await axios.post("/quotation/create", finalPDFData);

      toast.success("Quotation saved!");
    } catch (err) {
      toast.error("Error saving quotation");
    }
  };

  /*******************************************************
   * EXPORT PDF - QUOTATION (EXACT TEMPLATE MATCH - SINGLE PAGE)
   *******************************************************/
  const handleExportPDF = async () => {
    try {
      const finalPDFData = buildFinalPDFData();
      await generateQuotationPDF(finalPDFData, { companyLogos: COMPANY_LOGOS });
      toast.success("Premium PDF Exported!");
    } catch (err) {
      toast.error("Failed to export PDF");
    }
  };

  // ✅ 1) Total Own Damage Premium (WITHOUT Add-ons) = Basic OD - NCB only
  const totalOwnDamagePremium =
    (formData.premiumDetails.subTotalBasic || 0) - (formData.discounts.ncbAmount || 0);

  // ✅ 2) Net Own Damage Premium (A) = Total OD + Add-on Amount
  const netOwnDamagePremium = totalOwnDamagePremium + (formData.addon_amount || 0);

    /*******************************************************
   *                     UI START
  *******************************************************/
  return (
    <div className="quotation-builder">

      {/* ===== HEADER ===== */}
      <div className="admin-header">
        <div className="container">
          <div className="private-quotation-header">
            <div className="pq-header-left">
            </div>

            <div className="pq-header-center">
              <h1>Motor – Private Car Insurance Quotation Module</h1>
              <p>Create private car insurance quotations</p>
            </div>

            <div className="pq-header-right">
              <div className="header-actions">
                <button className="btn btn-secondary" onClick={handleExportPDF}>
                  <FaFilePdf /> Export PDF
                </button>
                <button className="btn btn-primary" onClick={handleSave}>
                  <FaSave /> Save Quotation
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== MAIN BODY ===== */}
      <div className="admin-content">
        <div className="container">

          <div className="quotation-form-grid">

            {/* ================= LEFT SIDE ================= */}
            <div className="quotation-form-section">

              {/* **************************************
                  CUSTOMER DETAILS
              ************************************** */}
              <div className="form-section card">
                <h2>
                  <span className="section-heading-highlight">Customer Details</span>
                </h2>

                <div className="form-group">
                  <label>Insurance Company *</label>
                  <select
                    className="form-select"
                    name="insuranceCompany"
                    value={formData.insuranceCompany}
                    onChange={handleInputChange}
                  >
                    <option value="">SELECT</option>
                    {insuranceCompanies.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      className="form-input input-uppercase"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Mobile</label>
                    <input
                      className="form-input"
                      name="customerMobile"
                      value={formData.customerMobile}
                      onChange={handleMobileChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    className="form-input input-lowercase"
                    name="customerEmail"
                    value={formData.customerEmail}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* **************************************
                  VEHICLE DETAILS
              ************************************** */}
              <div className="form-section card">
                <h2>
                  <span className="section-heading-highlight">Vehicle Details</span>
                </h2>

                <div className="form-row registration-type-row">
                  <div className="form-group">
                    <label>BH Registration</label>
                    <div style={{ display:'flex', alignItems:'center', gap:'1rem' }}>
                      <label><input type="radio" name="bhReg" value="YES"
                        checked={bhRegistration === "YES"}
                        onChange={(e)=>{ setBhRegistration(e.target.value); setSpecialRegNo(false); }}
                        disabled={specialRegNo}
                      /> YES</label>

                      <label><input type="radio" name="bhReg" value="NO"
                        checked={bhRegistration === "NO"}
                        onChange={(e)=> setBhRegistration(e.target.value)}
                        disabled={specialRegNo}
                      /> NO</label>

                      <label><input type="checkbox" name="specialReg"
                        checked={specialRegNo}
                        onChange={(e)=>{ setSpecialRegNo(e.target.checked); setFormData(prev=>({...prev, vehicleDetails:{...prev.vehicleDetails, registrationNumber:""}})); }}
                        disabled={bhRegistration === "YES"}
                      /> Special Reg No.</label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Registration No.</label>
                    <input
                      type="text"
                      className="form-input input-uppercase"
                      name="registrationNumber"
                      value={formData.vehicleDetails.registrationNumber}
                      onChange={(e) => {
                        const formatted = formatRegistration(
                          e.target.value,
                          bhRegistration === "YES",
                          specialRegNo === true
                        );
                        setFormData((prev) => ({
                          ...prev,
                          vehicleDetails: {
                            ...prev.vehicleDetails,
                            registrationNumber: formatted,
                          },
                        }));
                      }}
                      placeholder={
                        specialRegNo ? "0001"
                        : bhRegistration === "YES" ? "XXBH1234A"
                        : "XXMH12345"
                      }
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Make</label>
                    <input
                      className="form-input input-uppercase"
                      name="vehicleDetails.make"
                      value={formData.vehicleDetails.make}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Model</label>
                    <input
                      className="form-input input-uppercase"
                      name="vehicleDetails.model"
                      value={formData.vehicleDetails.model}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Variant</label>
                  <input
                    className="form-input input-uppercase"
                    name="vehicleDetails.variant"
                    value={formData.vehicleDetails.variant}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Fuel</label>
                  <select
                    className="form-select"
                    name="vehicleDetails.fuelType"
                    value={formData.vehicleDetails.fuelType}
                    onChange={handleInputChange}
                  >
                    <option value="">SELECT</option>
                    <option value="PETROL">PETROL</option>
                    <option value="DIESEL">DIESEL</option>
                    <option value="CNG">CNG</option>
                    <option value="ELECTRIC">ELECTRIC</option>
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Registration Year</label>
                    <select
                      className="form-select"
                      name="vehicleDetails.registrationYear"
                      value={formData.vehicleDetails.registrationYear}
                      onChange={handleInputChange}
                    >
                      <option value="">SELECT</option>
                      {Array.from({ length: currentYear - 1989 }, (_, i) => currentYear - i).map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>CC</label>
                    <input
                      className="form-input"
                      name="vehicleDetails.cubicCapacity"
                      value={formData.vehicleDetails.cubicCapacity}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Seating Capacity</label>
                    <input
                      className="form-input"
                      name="vehicleDetails.seatingCapacity"
                      value={formData.vehicleDetails.seatingCapacity}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Policy Expiry Date</label>
                    <input
                      type="date"
                      className="form-input"
                      name="vehicleDetails.policyExpiryDate"
                      value={formData.vehicleDetails.policyExpiryDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Previous Claim Status (NCB)</label>
                  <select
                    className="form-select"
                    name="vehicleDetails.previousClaimPercent"
                    value={formData.vehicleDetails.previousClaimPercent}
                    onChange={handleInputChange}
                  >
                    <option value="">SELECT</option>
                    <option value="CLAIM TAKEN">CLAIM TAKEN</option>
                    <option value="0">0%</option>
                    <option value="20">20%</option>
                    <option value="25">25%</option>
                    <option value="35">35%</option>
                    <option value="45">45%</option>
                    <option value="50">50%</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Policy Type</label>
                  <select
                    className="form-select"
                    name="vehicleDetails.policyType"
                    value={formData.vehicleDetails.policyType}
                    onChange={handleInputChange}
                  >
                    <option value="">SELECT</option>
                    <option value="COMPREHENSIVE">COMPREHENSIVE</option>
                    <option value="OWN DAMAGE ONLY">OWN DAMAGE ONLY</option>
                    <option value="THIRD PARTY">THIRD PARTY</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>City</label>
                  <input
                    className="form-input input-uppercase"
                    name="vehicleDetails.city"
                    value={formData.vehicleDetails.city}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Zone</label>
                    <input
                      className="form-input"
                      readOnly
                      style={{ background: "#eee" }}
                      value={formData.vehicleDetails.zone}
                    />
                  </div>

                  <div className="form-group">
                    <label>Vehicle Age</label>
                    <input
                      className="form-input"
                      readOnly
                      style={{ background: "#eee" }}
                      value={formData.vehicleDetails.vehicleAge + " years"}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Hypothecation / Lease</label>
                  <select
                    className="form-select"
                    name="vehicleDetails.hypothecationLease"
                    value={formData.vehicleDetails.hypothecationLease}
                    onChange={handleInputChange}
                  >
                    <option value="">-</option>
                    <option value="YES">Yes</option>
                    <option value="NO">No</option>
                  </select>
                </div>
              </div>

              {/* **************************************
                  IDV DETAILS
              ************************************** */}
              <div className="form-section card">
                <h2>
                  <span className="section-heading-highlight">IDV Details</span>
                </h2>

                <div className="form-group">
                  <label>Vehicle IDV</label>
                  <input
                    className="form-input"
                    name="idvDetails.vehicleIDV"
                    value={formData.idvDetails.vehicleIDV}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Electrical Accessories</label>
                  <input
                    className="form-input"
                    name="idvDetails.electrical"
                    value={formData.idvDetails.electrical}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Non-Electrical Accessories</label>
                  <input
                    className="form-input"
                    name="idvDetails.nonElectrical"
                    value={formData.idvDetails.nonElectrical}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Bi-Fuel IDV</label>
                  <input
                    className="form-input"
                    name="idvDetails.biFuel"
                    value={formData.idvDetails.biFuel}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Total IDV</label>
                  <input
                    className="form-input"
                    readOnly
                    style={{ background: "#eee" }}
                    value={formData.idvDetails.totalIDV}
                  />
                </div>
              </div>

              {/* **************************************
                  PREMIUM STRUCTURE START
              ************************************** */}
              <div className="form-section card">
                <h2>
                  <span className="section-heading-highlight">
                    Premium Structure (Based on Policy Type)
                  </span>
                </h2>

                {/* COMPREHENSIVE POLICY */}
                {formData.vehicleDetails.policyType === "COMPREHENSIVE" && (
                  <div className="policy-block">

                    <h3 className="premium-title">Own Damage Premium (A)</h3>

                    {/* OD RATE ROW */}
                    <div className="row-3-columns">
                      <div className="col">
                        <span>OD Rate % (Auto)</span>
                        <input
                          className="form-input small-input"
                          readOnly
                          style={{ background: "#eee" }}
                          value={formData.od_rate}
                        />
                      </div>

                      <div className="col">
                        <span>Rate Amount</span>
                        <input
                          className="form-input small-input"
                          readOnly
                          style={{ background: "#eee" }}
                          value={formData.od_rate_amount}
                        />
                      </div>

                      <div className="col">
                        <span>Discount %</span>
                        <input
                          className="form-input small-input"
                          name="od_discount"
                          value={formData.od_discount}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* BASIC OD */}
                    <div className="premium-row-line2">
                      <span className="highlight-title">Basic OD Premium (Vehicle)</span>
                      <input
                        className="form-input small-input"
                        readOnly
                        style={{ background: "#eee" }}
                        value={formData.od_final}
                      />
                    </div>

                    <div className="premium-row-line2">
                      <span>Elec. Accessories</span>
                      <input
                        className="form-input small-input"
                        readOnly
                        style={{ background: "#eee" }}
                        value={formData.premiumDetails.elecAccessories}
                      />
                    </div>

                    <div className="premium-row-line2">
                      <span>Non-Elec. Accessories</span>
                      <input
                        className="form-input small-input"
                        readOnly
                        style={{ background: "#eee" }}
                        value={formData.premiumDetails.nonElecAccessories}
                      />
                    </div>

                    <div className="premium-row-line2">
                      <span>Bi-Fuel Kit</span>
                      <input
                        className="form-input small-input"
                        readOnly
                        style={{ background: "#eee" }}
                        value={formData.premiumDetails.biFuelKit}
                      />
                    </div>

                    <div className="premium-row-line2">
                      <span className="highlight-title">Sub Total (Basic Premium)</span>
                      <input
                        className="form-input small-input"
                        readOnly
                        style={{ background: "#eee" }}
                        value={formData.premiumDetails.subTotalBasic}
                      />
                    </div>

                    {/* ===== DISCOUNTS ===== */}
                    <p className="highlight-title">Discount / Deductibles</p>

                    {/* Anti-Theft */}
                    <div className="premium-row-line2">
                      <span>Anti Theft</span>
                      <input
                        className="form-input small-input"
                        name="discountAntiTheft"
                        value={formData.premiumDetails.discountAntiTheft}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            premiumDetails: {
                              ...prev.premiumDetails,
                              discountAntiTheft: Number(e.target.value) || 0,
                            },
                          }))
                        }
                      />
                    </div>

                    {/* NCB Auto */}
<div className="row-3-columns" style={{ gridTemplateColumns: "1fr" }}>

  <div className="col" style={{ display: "flex", flexDirection: "column" }}>

    {/* NCB % (TEXT ONLY) */}
    <span style={{ fontWeight: "400", marginBottom: "6px" }}>
      No Claim Bonus ( {formData.ncb || 0}% )
    </span>

    {/* NCB AMOUNT BELOW (NO LABEL) */}
    <input
      className="form-input small-input"
      readOnly
      style={{ background: "#eee", width: "150px" }}
      value={formData.discounts.ncbAmount || 0}
    />
  </div>

</div>

                    <div className="premium-row-line2">
                      <span className="highlight-title">Sub Total (Deductibles)</span>
                      <input
                        className="form-input small-input"
                        readOnly
                        style={{ background: "#eee" }}
                        value={formData.premiumDetails.subTotalDiscounts}
                      />
                    </div>

                    {/* TOTAL OWN DAMAGE PREMIUM ROW */}
<div className="row-3-columns" style={{ gridTemplateColumns: "1fr", marginTop: "12px" }}>
  <div className="col" style={{ display: "flex", flexDirection: "column" }}>

    <span style={{ fontWeight: "700", marginBottom: "6px" }}>
      Total Own Damage Premium
    </span>

    <input
      className="form-input small-input"
      readOnly
      style={{ background: "#eee", width: "150px" }}
      value={totalOwnDamagePremium || 0}
    />

  </div>
</div>


                    {/* ⭐⭐ ADD-ON RATE + ADD-ON AMOUNT (Under Net OD) ⭐⭐ */}
                    <div className="form-row">

                      <div className="form-group">
                        <label>Add-on Rate (%)</label>
                        <input
                          className="form-input"
                          name="addon_rate"
                          value={formData.addon_rate}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Add-on Amount (₹)</label>
                        <input
                          className="form-input"
                          readOnly
                          style={{ background: "#eee" }}
                          value={formData.addon_amount}
                        />
                      </div>

                    </div>

                    <h3 className="premium-title net-own-damage-title">Net Own Damage Premium (A)</h3>

<div className="premium-row-line2">
  <input
    className="form-input small-input"
    readOnly
    style={{ background: "#eee" }}
    value={netOwnDamagePremium || 0}
  />
</div>

                    {/* ================= Liability Premium (B) ================= */}
                    {formData.vehicleDetails.policyType === "COMPREHENSIVE" && (
                    <>
                    <h3>Liability Premium (B)</h3>

                    {/* THIRD PARTY BASE PREMIUM (TP) */}
<div className="premium-row-line2">
  <span><strong>Third Party Base Premium (TP)</strong></span>
  <input
    className="form-input small-input"
    readOnly
    style={{ background: "#eee" }}
    value={premium.thirdParty || 0}
  />
</div>

                    <div className="premium-row-line2">
  <span>Bi-Fuel Kit</span>
  <input
    className="form-input small-input"
    readOnly
    style={{ background: "#eee" }}
    value={formData.liab_bifuel || 0}
  />
</div>

                    <div className="premium-row-line2">
  <span><strong>PA Owner Driver (₹15,00,000)</strong></span>
  <input
    className="form-input small-input"
    name="liab_pa_owner"
    value={formData.liab_pa_owner || ""}
    onChange={handleInputChange}
  />
</div>

<div className="premium-row-line2">
  <span><strong>PA Unnamed Passenger</strong></span>
  <input
    className="form-input small-input"
    name="liab_pa_passenger"
    value={formData.liab_pa_passenger || ""}
    onChange={handleInputChange}
  />
</div>

<div className="premium-row-line2">
  <span>PA Paid Driver</span>
  <input
    className="form-input small-input"
    name="liab_pa_driver"
    value={formData.liab_pa_driver || ""}
    onChange={handleInputChange}
  />
</div>

                    <h3 className="legal-liability-title">Legal Liability</h3>

<div className="premium-row-line2">
  <span><strong>LL to Paid Driver</strong></span>
  <input
    className="form-input small-input"
    name="ll_paid_driver"
    value={formData.ll_paid_driver || ""}
    onChange={handleInputChange}
  />
</div>

<div className="premium-row-line2">
  <span>LL to Employees</span>
  <input
    className="form-input small-input"
    name="ll_employees"
    value={formData.ll_employees || ""}
    onChange={handleInputChange}
  />
</div>

                    <p className="net-title">Net Liability Premium (B)</p>

<div className="premium-row-line2">
  <input
    className="form-input small-input"
    readOnly
    style={{ background: "#eee" }}
    value={formData.net_liability_B || 0}
  />
</div>
                    <hr />

                    <p className="net-title"><strong>Total Premium (A + B)</strong></p>
                    <div className="premium-row-line2">
                      <input
                        className="form-input small-input"
                        readOnly
                        style={{ background: "#eee", fontWeight: "bold" }}
                        value={netOwnDamagePremium + (formData.net_liability_B || 0)}
                      />
                    </div>

                    <>
                      <div className="premium-row-line2 bold-row">
                        <span>SGST (9%)</span>
                        <input
                          className="form-input small-input"
                          type="text"
                          readOnly
                          style={{ background: "#eee" }}
                          value={formData.taxes.sgst || 0}
                        />
                      </div>

                      <div className="premium-row-line2 bold-row">
                        <span>CGST (9%)</span>
                        <input
                          className="form-input small-input"
                          type="text"
                          readOnly
                          style={{ background: "#eee" }}
                          value={formData.taxes.cgst || 0}
                        />
                      </div>

                      <div className="premium-row-line2 bold-row gross-premium-paid-row">
                        <span><strong>Gross Premium Paid</strong></span>
                        <input
                          className="form-input small-input"
                          readOnly
                          style={{ background: "#eee" }}
                          value={formData.premiumDetails.grossPremium || 0}
                        />
                      </div>
                    </>
                    </>
                    )}

                  </div>
                )}

                {/* ========================== OWN DAMAGE ONLY ========================== */}
{formData.vehicleDetails.policyType === "OWN DAMAGE ONLY" && (
  <div className="policy-block">

    <h3 className="premium-title">Own Damage Premium (A)</h3>

    {/* OD RATE ROW */}
    <div className="row-3-columns">
      <div className="col">
        <span>OD Rate % (Auto)</span>
        <input
          className="form-input small-input"
          readOnly
          style={{ background: "#eee" }}
          value={formData.od_rate}
        />
      </div>

      <div className="col">
        <span>Rate Amount</span>
        <input
          className="form-input small-input"
          readOnly
          style={{ background: "#eee" }}
          value={formData.od_rate_amount}
        />
      </div>

      <div className="col">
        <span>Discount %</span>
        <input
          className="form-input small-input"
          name="od_discount"
          value={formData.od_discount}
          onChange={handleInputChange}
        />
      </div>
    </div>

    {/* BASIC OD */}
    <div className="premium-row-line2">
      <span className="highlight-title">Basic OD Premium (Vehicle)</span>
      <input
        className="form-input small-input"
        readOnly
        style={{ background: "#eee" }}
        value={formData.od_final}
      />
    </div>

    <div className="premium-row-line2">
      <span>Elec. Accessories</span>
      <input
        className="form-input small-input"
        readOnly
        style={{ background: "#eee" }}
        value={formData.premiumDetails.elecAccessories}
      />
    </div>

    <div className="premium-row-line2">
      <span>Non-Elec. Accessories</span>
      <input
        className="form-input small-input"
        readOnly
        style={{ background: "#eee" }}
        value={formData.premiumDetails.nonElecAccessories}
      />
    </div>

    <div className="premium-row-line2">
      <span>Bi-Fuel Kit</span>
      <input
        className="form-input small-input"
        readOnly
        style={{ background: "#eee" }}
        value={formData.premiumDetails.biFuelKit}
      />
    </div>

    <div className="premium-row-line2">
      <span className="highlight-title">Sub Total (Basic Premium)</span>
      <input
        className="form-input small-input"
        readOnly
        style={{ background: "#eee" }}
        value={formData.premiumDetails.subTotalBasic}
      />
    </div>


    {/* ===== DISCOUNTS ===== */}
    <p className="highlight-title">Discount / Deductibles</p>

    {/* Anti-Theft */}
    <div className="premium-row-line2">
      <span>Anti Theft</span>
      <input
        className="form-input small-input"
        name="discountAntiTheft"
        value={formData.premiumDetails.discountAntiTheft}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            premiumDetails: {
              ...prev.premiumDetails,
              discountAntiTheft: Number(e.target.value) || 0,
            },
          }))
        }
      />
    </div>


    {/* NCB Auto */}
<div className="row-3-columns" style={{ gridTemplateColumns: "1fr" }}>
  <div className="col" style={{ display: "flex", flexDirection: "column" }}>
    <span style={{ fontWeight: "400", marginBottom: "6px" }}>
      No Claim Bonus ( {formData.ncb || 0}% )
    </span>

    <input
      className="form-input small-input"
      readOnly
      style={{ background: "#eee", width: "150px" }}
      value={formData.discounts.ncbAmount || 0}
    />
  </div>
</div>

    <div className="premium-row-line2">
      <span className="highlight-title">Sub Total (Deductibles)</span>
      <input
        className="form-input small-input"
        readOnly
        style={{ background: "#eee" }}
        value={formData.premiumDetails.subTotalDiscounts}
      />
    </div>


    {/* TOTAL OWN DAMAGE PREMIUM */}
<div className="row-3-columns" style={{ gridTemplateColumns: "1fr", marginTop: "12px" }}>
  <div className="col" style={{ display: "flex", flexDirection: "column" }}>

    <span style={{ fontWeight: "700", marginBottom: "6px" }}>
      Total Own Damage Premium
    </span>

    <input
      className="form-input small-input"
      readOnly
      style={{ background: "#eee", width: "150px" }}
      value={totalOwnDamagePremium || 0}
    />

  </div>
</div>

    {/* ⭐⭐ ADD-ON RATE + ADD-ON AMOUNT ⭐⭐ */}
    <div className="form-row">
      <div className="form-group">
        <label>Add-on Rate (%)</label>
        <input
          className="form-input"
          name="addon_rate"
          value={formData.addon_rate}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group">
        <label>Add-on Amount (₹)</label>
        <input
          className="form-input"
          readOnly
          style={{ background: "#eee" }}
          value={formData.addon_amount}
        />
      </div>
    </div>

    <h3 className="premium-title net-own-damage-title">Net Own Damage Premium (A)</h3>

    <div className="premium-row-line2">
      <input
        className="form-input small-input"
        readOnly
        style={{ background: "#eee" }}
        value={netOwnDamagePremium || 0}
      />
    </div>

  </div>
)}

                {/* ================= THIRD PARTY POLICY (LIABILITY ONLY) ================= */}
{formData.vehicleDetails.policyType === "THIRD PARTY" && (
  <div className="policy-block">

    <h3>Liability Premium (B)</h3>

    {/* THIRD PARTY BASE PREMIUM */}
    <div className="premium-row-line2">
      <span><strong>Third Party Base Premium (TP)</strong></span>
      <input
        className="form-input small-input"
        readOnly
        style={{ background: "#eee" }}
        value={premium.thirdParty || 0}
      />
    </div>

    <div className="premium-row-line2">
      <span>Bi-Fuel Kit</span>
      <input
        className="form-input small-input"
        readOnly
        style={{ background: "#eee" }}
        value={formData.liab_bifuel || 0}
      />
    </div>

    <div className="premium-row-line2">
      <span><strong>PA Owner Driver (₹15,00,000)</strong></span>
      <input
        className="form-input small-input"
        name="liab_pa_owner"
        value={formData.liab_pa_owner || ""}
        onChange={handleInputChange}
      />
    </div>

    <div className="premium-row-line2">
      <span><strong>PA Unnamed Passenger</strong></span>
      <input
        className="form-input small-input"
        name="liab_pa_passenger"
        value={formData.liab_pa_passenger || ""}
        onChange={handleInputChange}
      />
    </div>

    <div className="premium-row-line2">
      <span>PA Paid Driver</span>
      <input
        className="form-input small-input"
        name="liab_pa_driver"
        value={formData.liab_pa_driver || ""}
        onChange={handleInputChange}
      />
    </div>

    <h3 className="legal-liability-title">Legal Liability</h3>

    <div className="premium-row-line2">
      <span><strong>LL to Paid Driver</strong></span>
      <input
        className="form-input small-input"
        name="ll_paid_driver"
        value={formData.ll_paid_driver || ""}
        onChange={handleInputChange}
      />
    </div>

    <div className="premium-row-line2">
      <span>LL to Employees</span>
      <input
        className="form-input small-input"
        name="ll_employees"
        value={formData.ll_employees || ""}
        onChange={handleInputChange}
      />
    </div>

    <p className="net-title">Net Liability Premium (B)</p>

    <div className="premium-row-line2">
      <input
        className="form-input small-input"
        readOnly
        style={{ background: "#eee" }}
        value={formData.net_liability_B || 0}
      />
    </div>

  </div>
)}
</div>
              {/* END PREMIUM STRUCTURE */}

              {/* **************************************
                  ADD-ONS SECTION
              ************************************** */}
              {(formData.vehicleDetails.policyType === "COMPREHENSIVE" || formData.vehicleDetails.policyType === "OWN DAMAGE ONLY") && (
              <div className="form-section card">
                <h2>
                  <span className="section-heading-highlight">Add-ons</span>
                </h2>

                <div className="addons-grid">
                  {Object.keys(formData.addOns)
                    .flatMap((name) => {
                      const labelValue = ADDON_LABELS[name];
                      if (Array.isArray(labelValue)) {
                        return labelValue.map((text, idx) => ({
                          key: `${name}-${idx}`,
                          name,
                          label: text,
                        }));
                      }
                      return [
                        {
                          key: name,
                          name,
                          label: labelValue || name.toUpperCase().replace(/_/g, " "),
                        },
                      ];
                    })
                    .map(({ key, name, label }) => (
                      <label key={key} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.addOns[name].included}
                          onChange={(e) => handleAddOnChange(name, e.target.checked)}
                        />

                        <span>{label}</span>

                        {formData.addOns[name].included && (
                          <span className="addon-premium">₹{formData.addOns[name].premium}</span>
                        )}
                      </label>
                    ))}
                </div>
              </div>
              )}
            </div>

            {/* ================= RIGHT SIDE SUMMARY ================= */}
            <div className="premium-summary-section">
              <div className="premium-summary card">

                <h2>Premium Summary</h2>

                <div className="premium-breakdown">

                  {/* A. Own Damage Section */}
                  {(formData.vehicleDetails.policyType === "COMPREHENSIVE" || formData.vehicleDetails.policyType === "OWN DAMAGE ONLY") && (
                  <div className="premium-section">
                    <h3 className="premium-section-title">A. Own Damage Section</h3>
                    
                    <div className="premium-row">
                      <span>Basic Own Damage</span>
                      <span>₹{(formData.premiumDetails.subTotalBasic || 0).toLocaleString('en-IN')}</span>
                    </div>

                    <div className="premium-row discount">
                      <span>No Claim Bonus</span>
                      <span>-₹{(formData.discounts.ncbAmount || 0).toLocaleString('en-IN')}</span>
                    </div>

                    <div className="premium-row">
                      <span>Total Own Damage</span>
                      <span>₹{totalOwnDamagePremium.toLocaleString('en-IN')}</span>
                    </div>

                    <div className="premium-row">
                      <span>Add-on{(() => {
                        const selectedAddOns = Object.keys(formData.addOns)
                          .filter(key => formData.addOns[key].included)
                          .map(key => ADDON_SUMMARY_CODES[key])
                          .filter(Boolean);
                        return selectedAddOns.length > 0 ? ` (${selectedAddOns.join(', ')})` : '';
                      })()}</span>
                      <span>₹{(formData.addon_amount || 0).toLocaleString('en-IN')}</span>
                    </div>

                    <div className="premium-row highlight">
                      <span>Net Own Damage</span>
                      <span>₹{netOwnDamagePremium.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  )}

                  {(formData.vehicleDetails.policyType === "COMPREHENSIVE" || formData.vehicleDetails.policyType === "THIRD PARTY") && (
                  <>
                  {(formData.vehicleDetails.policyType === "COMPREHENSIVE" || formData.vehicleDetails.policyType === "OWN DAMAGE ONLY") && (
                  <div className="premium-divider"></div>
                  )}

                  {/* B. Liability Section */}
                  <div className="premium-section">
                    <h3 className="premium-section-title">B. Liability Section</h3>
                    
                    <div className="premium-row">
                      <span>Third Party</span>
                      <span>₹{(premium.thirdParty || 0).toLocaleString('en-IN')}</span>
                    </div>

                    <div className="premium-row">
                      <span><strong>PA Owner Driver</strong></span>
                      <span>₹{(Number(formData.liab_pa_owner) || 0).toLocaleString('en-IN')}</span>
                    </div>

                    <div className="premium-row">
                      <span><strong>PA Unnamed Passenger</strong></span>
                      <span>₹{(Number(formData.liab_pa_passenger) || 0).toLocaleString('en-IN')}</span>
                    </div>

                    <div className="premium-row">
                      <span>PA Paid Driver</span>
                      <span>₹{(Number(formData.liab_pa_driver) || 0).toLocaleString('en-IN')}</span>
                    </div>

                    <div className="premium-row">
                      <span><strong>LL to Paid Driver</strong></span>
                      <span>₹{(Number(formData.ll_paid_driver) || 0).toLocaleString('en-IN')}</span>
                    </div>

                    <div className="premium-row">
                      <span>LL to Employees</span>
                      <span>₹{(Number(formData.ll_employees) || 0).toLocaleString('en-IN')}</span>
                    </div>

                    <div className="premium-row highlight">
                      <span>Net Liability</span>
                      <span>₹{(formData.net_liability_B || 0).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  </>
                  )}

                  <div className="premium-divider"></div>

                  {/* C. Final Amounts */}
                  <div className="premium-section">
                    <h3 className="premium-section-title">C. Final Amounts</h3>
                    
                    {(() => {
                      const totalPremium = formData.vehicleDetails.policyType === "THIRD PARTY" 
                        ? (formData.net_liability_B || 0)
                        : formData.vehicleDetails.policyType === "OWN DAMAGE ONLY"
                        ? netOwnDamagePremium
                        : netOwnDamagePremium + (formData.net_liability_B || 0);
                      
                      return (
                        <>
                          <div className="premium-row">
                            <span>Total Premium</span>
                            <span>₹{totalPremium.toLocaleString('en-IN')}</span>
                          </div>

                          <div className="premium-row">
                            <span>SGST (9%)</span>
                            <span>₹{(formData.taxes.sgst || 0).toLocaleString('en-IN')}</span>
                          </div>

                          <div className="premium-row">
                            <span>CGST (9%)</span>
                            <span>₹{(formData.taxes.cgst || 0).toLocaleString('en-IN')}</span>
                          </div>

                          <div className="premium-row total">
                            <span><strong>Gross Premium Paid</strong></span>
                            <span>₹{(formData.premiumDetails.grossPremium || 0).toLocaleString('en-IN')}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

    </div>
  );
};

export default QuotationBuilder;
